import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Minus, Pencil, Layers, ClipboardCheck, FileText, Trash2 } from "lucide-react";
import { createLogger, isTestCompanyLimitError } from "@ict/shared";
import { useToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useCachedProject } from "@/hooks/useCachedProject";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useProjectDownload } from "@/hooks/useProjectDownload";
import { OfflineBanner } from "@/components/ui/OfflineBanner";
import { DELETE_PROJECT_MUTATION, PROJECTS_QUERY, PROJECT_QUERY } from "@/lib/graphql/projects";
import {
  useEquipmentTypes,
  groupEquipmentsByCategory,
  getEquipmentCategoryLabel,
  CATEGORY_ORDER,
  CREATE_EQUIPMENT_MUTATION,
  DELETE_EQUIPMENT_MUTATION,
  UPDATE_EQUIPMENT_COUNT_MUTATION,
  type EquipmentStatusItem,
  type EquipmentType,
  type EquipmentCategoryType,
  type UpdateEquipmentCountData,
  type UpdateEquipmentCountVariables,
} from "@/lib/graphql/equipment";
import { PROJECT_TYPE_LABELS, PROJECT_STATUS_LABELS } from "@/lib/constants/project";
import { formatDateShort } from "@/lib/utils/date";
import { EquipmentCountEditModal } from "@/components/EquipmentCountEditModal";
import { DemoLimitModal } from "@/components/onboarding/DemoLimitModal";
import { DEMO_STATUS, type DemoStatusData } from "@/lib/graphql/user";
import { addToSyncQueue, cancelEquipmentCreate, cleanupEquipmentQueue } from "@/lib/db/changes";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";
import { cacheProject, getCachedProject } from "@/lib/db/projects";
import { removeCachedMachine } from "@/lib/db/machines";
import { cleanupEquipmentPhotos } from "@/lib/db/photos";
import { useSyncQueueContext } from "@/contexts/SyncQueueContext";
import { createOfflineEquipmentFromTemplate } from "@/lib/db/templates";

const logger = createLogger({ module: "ProjectDetailPage" });

/** 카테고리별 모바일 색상 매핑 */
const CATEGORY_COLORS: Record<
  EquipmentCategoryType,
  {
    bar: string; // 카테고리 구분 바
    header: string; // 헤더 1행 배경
    light: string; // 헤더 2행 배경
    lightActive: string; // 헤더 2행 active
    accent: string; // 포인트 텍스트 색상
    badge: string; // 문서 번호 배지 배경
    badgeText: string; // 문서 번호 배지 텍스트
    docActive: string; // 문서 행 active
  }
> = {
  COMMUNICATION: {
    bar: "bg-blue-500",
    header: "bg-blue-600",
    light: "bg-blue-50",
    lightActive: "active:bg-blue-100",
    accent: "text-blue-600",
    badge: "bg-blue-100",
    badgeText: "text-blue-600",
    docActive: "active:bg-blue-50",
  },
  BROADCAST: {
    bar: "bg-green-500",
    header: "bg-green-600",
    light: "bg-green-50",
    lightActive: "active:bg-green-100",
    accent: "text-green-600",
    badge: "bg-green-100",
    badgeText: "text-green-600",
    docActive: "active:bg-green-50",
  },
  INFORMATION: {
    bar: "bg-purple-500",
    header: "bg-purple-600",
    light: "bg-purple-50",
    lightActive: "active:bg-purple-100",
    accent: "text-purple-600",
    badge: "bg-purple-100",
    badgeText: "text-purple-600",
    docActive: "active:bg-purple-50",
  },
  OTHER: {
    bar: "bg-slate-400",
    header: "bg-slate-500",
    light: "bg-slate-50",
    lightActive: "active:bg-slate-100",
    accent: "text-slate-500",
    badge: "bg-slate-100",
    badgeText: "text-slate-500",
    docActive: "active:bg-slate-50",
  },
};

/** 선택된 설비 정보 (수량 편집 모달용) */
interface SelectedEquipmentForCount {
  key: string;
  title: string;
  totalCount: number;
  checkCount: number;
}

/** 카테고리별 설비 그룹 */
interface CategoryGroup {
  category: EquipmentCategoryType;
  label: string;
  equipments: EquipmentStatusItem[];
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { refreshPendingCount } = useSyncQueueContext();

  // 설비별 접힘 상태 (문서 있는 설비만 기본 펼침)
  const [expandedEquipments, setExpandedEquipments] = useState<Set<string>>(new Set());
  const prevProjectIdRef = useRef<string | undefined>(undefined);
  const scrollPositionRef = useRef<number>(0);

  // 설비 추가 중인 상태 (키 + 이름)
  const [addingEquipment, setAddingEquipment] = useState<{
    key: string;
    title: string;
  } | null>(null);
  // update 함수에서 machineKey에 접근하기 위한 ref
  const addingEquipmentRef = useRef<{ key: string; title: string } | null>(null);

  // 수량 편집 모달 상태
  const [isCountModalOpen, setIsCountModalOpen] = useState(false);
  const [selectedForCount, setSelectedForCount] = useState<SelectedEquipmentForCount | null>(null);

  // 삭제 모드 상태 (현재 삭제 모드인 설비 타입 key)
  const [deleteModeKey, setDeleteModeKey] = useState<string | null>(null);
  // 삭제 중인 문서 ID (스피너 표시용)
  const [deletingMachineId, setDeletingMachineId] = useState<string | null>(null);

  // 데모 한도 모달 상태
  const [showLimitModal, setShowLimitModal] = useState(false);

  // 데모 상태 조회 (에러 시에만 호출)
  const [fetchDemoStatus, { data: demoData }] = useLazyQuery<DemoStatusData>(DEMO_STATUS);

  // 오프라인 상태 확인
  const { isOffline, isLoading: networkLoading } = useNetworkStatus();

  // 프로젝트 데이터 다운로드 훅 (자동 다운로드용)
  const {
    downloading: isDownloading,
    progress: downloadProgress,
    total: downloadTotal,
    downloaded: downloadedCount,
    downloadProject,
  } = useProjectDownload();

  // 프로젝트 + 설비 현황 조회 (오프라인 지원)
  const { project, equipmentStatus, loading, error, isFromCache, refetch, reloadCache } =
    useCachedProject(id);

  // 프로젝트 전환 시 펼침 상태 리셋 + 문서 있는 설비 자동 펼침
  useEffect(() => {
    if (id !== prevProjectIdRef.current) {
      prevProjectIdRef.current = id;
      setExpandedEquipments(new Set());
    }
    if (equipmentStatus?.equipments) {
      const withDocs = equipmentStatus.equipments
        .filter((eq: EquipmentStatusItem) => (eq.docsIds?.length ?? 0) > 0)
        .map((eq: EquipmentStatusItem) => eq.key);
      setExpandedEquipments((prev) => {
        const next = new Set(prev);
        for (const key of withDocs) {
          next.add(key);
        }
        return next;
      });
    }
  }, [id, equipmentStatus]);

  // 자동 다운로드: 온라인이고, 프로젝트 로드 완료 시 자동으로 다운로드
  const [autoDownloadTriggered, setAutoDownloadTriggered] = useState(false);
  useEffect(() => {
    if (
      !networkLoading &&
      !isOffline &&
      !loading &&
      project &&
      id &&
      !autoDownloadTriggered &&
      !isDownloading
    ) {
      setAutoDownloadTriggered(true);
      downloadProject(id);
    }
  }, [
    networkLoading,
    isOffline,
    loading,
    project,
    id,
    autoDownloadTriggered,
    isDownloading,
    downloadProject,
  ]);

  // 34개 설비 타입 조회
  const { data: typesData, loading: typesLoading } = useEquipmentTypes();

  const [deleteProject, { loading: deleting }] = useMutation(DELETE_PROJECT_MUTATION, {
    refetchQueries: [{ query: PROJECTS_QUERY }],
    onCompleted: () => {
      showToast("프로젝트가 삭제되었습니다.", "success");
      navigate("/projects");
    },
    onError: (err: Error) => {
      logger.error("프로젝트 삭제 실패:", err);
      showToast("프로젝트 삭제에 실패했습니다.", "error");
    },
  });

  // 수량 수정 mutation
  const [updateEquipmentCount, { loading: updating }] = useMutation<
    UpdateEquipmentCountData,
    UpdateEquipmentCountVariables
  >(UPDATE_EQUIPMENT_COUNT_MUTATION, {
    onCompleted: async (result) => {
      if (result.updateEquipmentCount.success) {
        showToast("수량이 수정되었습니다.", "success");
        setIsCountModalOpen(false);
        setSelectedForCount(null);
        scrollPositionRef.current = window.scrollY;
        await refetch();
        requestAnimationFrame(() => window.scrollTo(0, scrollPositionRef.current));
      } else {
        showToast(result.updateEquipmentCount.error || "수정에 실패했습니다.", "error");
      }
    },
    onError: (err: Error) => {
      logger.error("수량 수정 실패:", err);
      showToast("수량 수정에 실패했습니다.", "error");
    },
  });

  // 설비 생성 mutation
  const [createEquipment] = useMutation(CREATE_EQUIPMENT_MUTATION, {
    refetchQueries: [{ query: DEMO_STATUS }],
    update: (cache, { data }) => {
      if (!data?.createEquipment?.success || !id) return;

      const { equipments: newEquipments, checkCount: newCheckCount } = data.createEquipment;
      const newIds = (newEquipments || []).map((e: { id: string }) => e.id);
      const machineKey = addingEquipmentRef.current?.key;
      if (!machineKey || newIds.length === 0) return;

      try {
        const existing = cache.readQuery<{
          project: unknown;
          projectEquipmentStatus: {
            projectId: string;
            projectName: string;
            equipments: EquipmentStatusItem[];
          } | null;
        }>({
          query: PROJECT_QUERY,
          variables: { id },
        });
        if (!existing?.projectEquipmentStatus) return;

        const updatedEquipments = existing.projectEquipmentStatus.equipments.map(
          (eq: EquipmentStatusItem) => {
            if (eq.key === machineKey) {
              return {
                ...eq,
                docsIds: [...(eq.docsIds || []), ...newIds],
                docsCount: (eq.docsCount || 0) + newIds.length,
                checkCount: newCheckCount ?? eq.checkCount,
              };
            }
            return eq;
          }
        );

        cache.writeQuery({
          query: PROJECT_QUERY,
          variables: { id },
          data: {
            ...existing,
            projectEquipmentStatus: {
              ...existing.projectEquipmentStatus,
              equipments: updatedEquipments,
            },
          },
        });
      } catch (e) {
        // 캐시 업데이트 실패 시 무시 (UI는 다음 조회 시 정상 반영)
        logger.warn("설비 추가 캐시 업데이트 실패:", e);
      }
    },
    onCompleted: () => {
      showToast("1개 설비가 추가되었습니다.", "success");
      setAddingEquipment(null);
      addingEquipmentRef.current = null;
    },
    onError: (err) => {
      setAddingEquipment(null);
      addingEquipmentRef.current = null;
      logger.error("설비 추가 실패:", err);
      if (isTestCompanyLimitError(err)) {
        fetchDemoStatus();
        setShowLimitModal(true);
      } else {
        showToast("설비 추가에 실패했습니다.", "error");
      }
    },
  });

  // 삭제 중인 설비의 machineKey/machineId 추적 (onCompleted에서 큐 정리용)
  const deletingContextRef = useRef<{ machineKey: string; machineId: string } | null>(null);

  // 설비 삭제 mutation
  const [deleteEquipment] = useMutation(DELETE_EQUIPMENT_MUTATION, {
    onCompleted: async (result) => {
      const res = result.deleteEquipment;
      if (res.success) {
        // 온라인 삭제 성공 시 pending 큐 정리 (해당 설비의 미동기화 항목 제거)
        const ctx = deletingContextRef.current;
        if (ctx && id) {
          await cleanupEquipmentQueue(ctx.machineId, id, ctx.machineKey);
          await cleanupEquipmentPhotos(id, ctx.machineId);
          await refreshPendingCount();
        }
        deletingContextRef.current = null;
        showToast("설비가 삭제되었습니다.", "success");
        scrollPositionRef.current = window.scrollY;
        await refetch();
        requestAnimationFrame(() => window.scrollTo(0, scrollPositionRef.current));
        // 남은 문서가 0개면 삭제 모드 자동 해제
        if (res.remainingCount === 0) {
          setDeleteModeKey(null);
        }
      } else {
        deletingContextRef.current = null;
        showToast("설비 삭제에 실패했습니다.", "error");
      }
      setDeletingMachineId(null);
    },
    onError: (err: Error) => {
      deletingContextRef.current = null;
      logger.error("설비 삭제 실패:", err);
      showToast("설비 삭제에 실패했습니다.", "error");
      setDeletingMachineId(null);
    },
  });

  // 삭제 모드 토글
  const handleDeleteModeToggle = useCallback(
    (equipmentKey: string) => {
      if (deleteModeKey === equipmentKey) {
        // 삭제 모드 해제
        setDeleteModeKey(null);
      } else {
        // 삭제 모드 진입 + 자동 펼침
        setDeleteModeKey(equipmentKey);
        setExpandedEquipments((prev) => {
          const next = new Set(prev);
          next.add(equipmentKey);
          return next;
        });
      }
    },
    [deleteModeKey]
  );

  // 삭제 모드에서 문서 클릭 시 삭제 확인
  const handleDeleteDocClick = useCallback(
    async (machineKey: string, machineId: string, docTitle: string) => {
      if (!id || deletingMachineId) return;

      const confirmed = await confirm({
        title: "설비 삭제",
        message: `"${docTitle}"을(를) 삭제하시겠습니까?`,
        variant: "danger",
        confirmText: "삭제",
        cancelText: "취소",
      });

      if (!confirmed) return;

      setDeletingMachineId(machineId);

      if (isOffline) {
        // 오프라인 삭제 처리
        try {
          // 1. 오프라인 생성→즉시 삭제 상쇄 확인
          const cancelled = await cancelEquipmentCreate(machineId, id, machineKey);

          // 2. 해당 설비의 모든 pending 큐 항목 정리
          await cleanupEquipmentQueue(machineId, id, machineKey);

          // 2-1. 로컬 사진 메타데이터 정리
          await cleanupEquipmentPhotos(id, machineId);

          // 3. IndexedDB 캐시에서 설비 제거
          await removeCachedMachine(id, machineKey, machineId);

          // 3-1. 프로젝트 캐시의 equipmentStatus 업데이트
          const cachedProject = await getCachedProject(id);
          if (cachedProject?.equipmentStatus) {
            const updatedEquipments = cachedProject.equipmentStatus.equipments.map((eq) => {
              if (eq.key === machineKey) {
                return {
                  ...eq,
                  docsIds: (eq.docsIds || []).filter((docId: string) => docId !== machineId),
                  docsCount: Math.max(0, eq.docsCount - 1),
                  checkCount: Math.max(0, eq.checkCount - 1),
                };
              }
              return eq;
            });

            await cacheProject({
              ...cachedProject,
              equipmentStatus: { ...cachedProject.equipmentStatus, equipments: updatedEquipments },
              synced: false,
              cachedAt: Date.now(),
            });
          }

          // 4. 상쇄가 아닌 경우만 delete 큐 등록
          if (!cancelled) {
            await addToSyncQueue({
              type: "equipment",
              entityId: `${machineId}-delete`,
              operation: "delete",
              payload: { projectId: id, machineKey, machineId },
              projectId: id,
              docKey: machineKey,
              machineId,
            });
          }

          await refreshPendingCount();

          // 남은 문서 수 확인하여 삭제 모드 자동 해제
          const remainingDocs = (
            equipmentStatus?.equipments.find((eq) => eq.key === machineKey)?.docsIds || []
          ).filter((docId: string) => docId !== machineId);

          showToast("설비가 삭제되었습니다.", "success");
          scrollPositionRef.current = window.scrollY;
          await reloadCache();
          requestAnimationFrame(() => window.scrollTo(0, scrollPositionRef.current));

          if (remainingDocs.length === 0) {
            setDeleteModeKey(null);
          }
        } catch (err) {
          logger.error("오프라인 설비 삭제 실패:", err);
          showToast("설비 삭제에 실패했습니다.", "error");
        } finally {
          setDeletingMachineId(null);
        }
        return;
      }

      // 온라인 삭제 처리
      deletingContextRef.current = { machineKey, machineId };
      deleteEquipment({
        variables: {
          input: { projectId: id, machineKey, machineId },
        },
      });
    },
    [
      id,
      deletingMachineId,
      confirm,
      isOffline,
      deleteEquipment,
      equipmentStatus,
      showToast,
      reloadCache,
      refreshPendingCount,
    ]
  );

  // [+] 버튼 클릭 시 즉시 1개 설비 추가
  const handleAddClick = useCallback(
    async (machineKey: string, title: string) => {
      if (!id || addingEquipment) return;

      setExpandedEquipments((prev) => {
        const next = new Set(prev);
        next.add(machineKey);
        return next;
      });
      setAddingEquipment({ key: machineKey, title });
      addingEquipmentRef.current = { key: machineKey, title };

      if (isOffline) {
        try {
          const createdMachines = await createOfflineEquipmentFromTemplate(
            id,
            project?.type || "PERFORMANCE_CHECK",
            machineKey,
            1
          );

          if (!createdMachines) {
            showToast(
              "오프라인 설비 생성을 위한 템플릿이 없습니다. 온라인에서 다시 시도해주세요.",
              "warning"
            );
            setAddingEquipment(null);
            return;
          }

          const newMachineIds = createdMachines.map((m) => m.machine._id);

          if (equipmentStatus && project) {
            const updatedEquipments = equipmentStatus.equipments.map((eq) => {
              if (eq.key === machineKey) {
                return {
                  ...eq,
                  checkCount: eq.checkCount + 1,
                  docsCount: eq.docsCount + 1,
                  docsIds: [...(eq.docsIds || []), ...newMachineIds],
                };
              }
              return eq;
            });

            await cacheProject({
              ...project,
              equipmentStatus: {
                ...equipmentStatus,
                equipments: updatedEquipments,
              },
              synced: false,
              cachedAt: Date.now(),
            });
          }

          await addToSyncQueue({
            type: "equipment",
            entityId: machineKey,
            operation: "create",
            payload: { machineKey, count: 1 },
            projectId: id,
            docKey: machineKey,
            createdMachineIds: newMachineIds,
          });

          setAddingEquipment(null);
          showToast("1개 설비 문서가 생성되었습니다.", "success");
          scrollPositionRef.current = window.scrollY;
          await reloadCache();
          requestAnimationFrame(() => window.scrollTo(0, scrollPositionRef.current));
        } catch (err) {
          setAddingEquipment(null);
          logger.error("오프라인 설비 저장 실패:", err);
          showToast("설비 저장에 실패했습니다.", "error");
        }
        return;
      }

      createEquipment({
        variables: {
          input: { projectId: id, machineKey, count: 1 },
        },
      });
    },
    [
      addingEquipment,
      id,
      isOffline,
      equipmentStatus,
      project,
      createEquipment,
      showToast,
      reloadCache,
    ]
  );

  // 수량 영역 클릭 시 수량 편집 모달 열기
  const handleCountClick = useCallback((eq: EquipmentStatusItem) => {
    setSelectedForCount({
      key: eq.key,
      title: eq.title,
      totalCount: eq.totalCount,
      checkCount: eq.checkCount,
    });
    setIsCountModalOpen(true);
  }, []);

  // 수량 편집 모달 확인
  const handleCountConfirm = useCallback(
    (totalCount: number, checkCount: number) => {
      if (!selectedForCount || !id) return;
      updateEquipmentCount({
        variables: {
          input: {
            projectId: id,
            equipmentKey: selectedForCount.key,
            totalCount,
            checkCount,
          },
        },
      });
    },
    [selectedForCount, id, updateEquipmentCount]
  );

  // 수량 편집 모달 닫기
  const handleCountModalClose = useCallback(() => {
    if (!updating) {
      setIsCountModalOpen(false);
      setSelectedForCount(null);
    }
  }, [updating]);

  // 문서 클릭 시 점검 페이지로 이동
  const handleDocClick = useCallback(
    (docKey: string, machineId: string) => {
      navigate(`/projects/${id}/inspection/${docKey}/${machineId}`);
    },
    [navigate, id]
  );

  // 설비 토글
  const toggleEquipment = useCallback((equipmentKey: string) => {
    setExpandedEquipments((prev) => {
      const next = new Set(prev);
      if (next.has(equipmentKey)) {
        next.delete(equipmentKey);
      } else {
        next.add(equipmentKey);
      }
      return next;
    });
  }, []);

  // 카테고리별 그룹화된 설비 목록
  const categoryGroups = useMemo(() => {
    if (!typesData?.equipmentTypes) return [];

    const equipments: EquipmentStatusItem[] = equipmentStatus?.equipments
      ? equipmentStatus.equipments
      : typesData.equipmentTypes.map((et: EquipmentType) => ({
          key: et.key,
          title: et.title,
          totalCount: 0,
          checkCount: 0,
          docsCount: 0,
          ratio: et.ratio,
          unit: et.unit,
        }));

    const grouped = groupEquipmentsByCategory(equipments, typesData.equipmentTypes);

    return CATEGORY_ORDER.map((category: EquipmentCategoryType) => ({
      category,
      label: getEquipmentCategoryLabel(category),
      equipments: grouped[category],
    })).filter((group: CategoryGroup) => group.equipments.length > 0);
  }, [equipmentStatus, typesData]);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "프로젝트 삭제",
      message: "정말로 이 프로젝트를 삭제하시겠습니까?",
      variant: "danger",
      confirmText: "삭제",
      cancelText: "취소",
    });
    if (confirmed) {
      deleteProject({ variables: { id } });
    }
  };

  // 로딩 상태
  if (loading || typesLoading) {
    return <FullScreenSpinner />;
  }

  // 에러 상태
  if (error || !project) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4 p-4">
        <div className="rounded-full bg-red-100 p-3">
          <svg
            className="h-6 w-6 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <p className="text-center text-slate-600">프로젝트를 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate("/projects")}
          className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-100 pb-6">
      {/* 오프라인/캐시 배너 */}
      <OfflineBanner showCacheIndicator={isFromCache && !isOffline} />

      {/* 헤더 */}
      <div className="sticky top-0 z-10 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm font-medium text-slate-600 active:text-slate-900"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            목록
          </button>
          <div className="flex gap-1">
            {/* 수정 아이콘 버튼 */}
            <button
              onClick={() => navigate(`/projects/${id}/edit`)}
              disabled={isOffline}
              className={`rounded-lg p-2 ${
                isOffline ? "text-slate-300" : "text-slate-600 active:bg-slate-100"
              }`}
              aria-label="수정"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            {/* 삭제 아이콘 버튼 */}
            <button
              onClick={handleDelete}
              disabled={deleting || isOffline}
              className={`rounded-lg p-2 ${
                isOffline ? "text-slate-300" : "text-red-500 active:bg-red-50 disabled:opacity-50"
              }`}
              aria-label="삭제"
            >
              {deleting ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-red-200 border-t-red-500" />
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        {/* 프로젝트 헤더 카드 */}
        <div className="mb-3 rounded-2xl bg-white p-5 shadow-sm">
          {/* 메타 정보: 번호 + 유형 + 상태 */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {project.number && (
              <span className="text-sm font-semibold tracking-wide text-slate-400">
                {project.number}
              </span>
            )}
            {project.type && (
              <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
                  project.type === "PERFORMANCE_CHECK"
                    ? "bg-teal-50 text-teal-600"
                    : "bg-orange-50 text-orange-600"
                }`}
              >
                {PROJECT_TYPE_LABELS[project.type as keyof typeof PROJECT_TYPE_LABELS]}
              </span>
            )}
            <span
              className={`rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
                project.status === "IN_PROGRESS"
                  ? "bg-blue-50 text-blue-600"
                  : project.status === "COMPLETED"
                    ? "bg-green-50 text-green-600"
                    : "bg-slate-100 text-slate-500"
              }`}
            >
              {PROJECT_STATUS_LABELS[project.status as keyof typeof PROJECT_STATUS_LABELS] ||
                project.status ||
                "대기"}
            </span>
          </div>

          {/* 프로젝트명 */}
          <h1 className="text-[22px] font-extrabold leading-tight tracking-tight text-slate-900">
            {project.name}
          </h1>
          <p className="mt-1 text-xs text-slate-400">
            등록일 : {formatDateShort(project.createdAt)}
          </p>
        </div>

        {/* 위치 정보 카드 */}
        {project.address && (
          <div className="mb-3 rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex items-start gap-2">
              <svg
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p className="text-sm text-slate-700">{project.address}</p>
            </div>
          </div>
        )}

        {/* 일정 및 담당자 정보 카드 */}
        <div className="mb-3 rounded-2xl bg-white px-4 py-3 shadow-sm">
          <div className="space-y-1.5 text-[13px]">
            <div className="flex justify-between">
              <span className="text-slate-400">점검기간</span>
              <span className="font-medium text-slate-700">
                {formatDateShort(project.startDate)} ~ {formatDateShort(project.endDate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">점검담당자</span>
              <span className="font-medium text-slate-700">{project.checkManager || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">작성자</span>
              <span className="font-medium text-slate-700">{project.author || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">입회자</span>
              <span className="font-medium text-slate-700">{project.witness || "-"}</span>
            </div>
          </div>
        </div>

        {/* 메모 카드 */}
        {project.memo && (
          <div className="mb-3 rounded-2xl bg-white p-4 shadow-sm">
            <p className="mb-1 text-xs text-slate-400">메모</p>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{project.memo}</p>
          </div>
        )}

        {/* 다운로드 진행 상태 */}
        {isDownloading && (
          <div className="mb-3 rounded-2xl bg-blue-50 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700">오프라인 저장 중...</span>
              <span className="text-sm text-blue-600">
                {downloadedCount} / {downloadTotal}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-blue-200">
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${downloadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* 카테고리별 설비 목록 */}
        <div className="space-y-1">
          {categoryGroups.map(({ category, label, equipments }: CategoryGroup) => {
            const colors = CATEGORY_COLORS[category];
            return (
              <div key={category}>
                {/* 카테고리 구분자 */}
                <div className="flex items-center gap-3 pb-2 pt-5 first:pt-1">
                  <div className={`h-6 w-1 rounded-sm ${colors.bar}`} />
                  <span className="text-[15px] font-extrabold tracking-tight text-slate-700">
                    {label}
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
                </div>

                {/* 설비 타입 아코디언 목록 */}
                <div className="space-y-2">
                  {equipments.map((eq: EquipmentStatusItem, eqIndex: number) => {
                    const isEmpty = eq.totalCount === 0 && eq.docsCount === 0;
                    const docsIds = eq.docsIds || [];
                    const hasDocs = docsIds.length > 0;
                    const isExpanded = expandedEquipments.has(eq.key);

                    return (
                      <div key={eq.key} className="overflow-hidden rounded-xl bg-white shadow-sm">
                        {/* 설비 타입 헤더 (2행: 헤더 + 수량 밴드) */}
                        <div
                          className={`${
                            isEmpty ? "opacity-60" : ""
                          } ${updating ? "pointer-events-none opacity-50" : ""}`}
                        >
                          {/* 1행: 꺽쇠 + 번호 + 설비명 + [-] [+] */}
                          <div className={`flex items-center gap-1.5 ${colors.header} px-3 py-2.5`}>
                            <button
                              onClick={() => hasDocs && toggleEquipment(eq.key)}
                              className={`flex min-w-0 flex-1 items-center gap-1.5 ${
                                hasDocs ? "active:opacity-70" : ""
                              }`}
                              disabled={!hasDocs}
                            >
                              <div
                                className={`flex h-4 w-4 flex-shrink-0 items-center justify-center transition-transform duration-200 ${
                                  isExpanded ? "rotate-90" : ""
                                }`}
                              >
                                <svg
                                  className={`h-3 w-3 ${hasDocs ? "text-white" : "text-white/30"}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </div>
                              <span className="flex-shrink-0 text-[11px] font-bold tabular-nums text-white/50">
                                {String(eqIndex + 1).padStart(2, "0")}
                              </span>
                              <span className="truncate text-[15px] font-bold text-white">
                                {eq.title}
                              </span>
                            </button>
                            {/* [+] 설비 추가 버튼 */}
                            <button
                              onClick={() => handleAddClick(eq.key, eq.title)}
                              disabled={addingEquipment !== null || deleteModeKey !== null}
                              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                                addingEquipment !== null || deleteModeKey !== null
                                  ? "bg-white/10 text-white/30"
                                  : "bg-white/15 text-white active:bg-white/25"
                              }`}
                              aria-label={`${eq.title} 설비 추가`}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            {/* [-] 삭제 모드 토글 버튼 */}
                            <button
                              onClick={() => handleDeleteModeToggle(eq.key)}
                              disabled={!hasDocs || addingEquipment !== null}
                              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                                deleteModeKey === eq.key
                                  ? "bg-red-500 text-white"
                                  : !hasDocs || addingEquipment !== null
                                    ? "bg-white/10 text-white/30"
                                    : "bg-red-500/30 text-red-200 active:bg-red-500/50"
                              }`}
                              aria-label={`${eq.title} 설비 삭제 모드`}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                          </div>

                          {/* 2행: 아이콘+라벨+숫자 3분할 + 편집 아이콘 */}
                          <button
                            onClick={() => !isOffline && !deleteModeKey && handleCountClick(eq)}
                            disabled={isOffline || deleteModeKey !== null}
                            className={`flex w-full items-center justify-between ${colors.light} px-3 py-1.5 transition-colors ${
                              isOffline || deleteModeKey !== null
                                ? "opacity-50"
                                : colors.lightActive
                            }`}
                          >
                            <div className="flex flex-1 items-center justify-end gap-3">
                              <span className="flex items-center gap-1 text-slate-400">
                                <Layers className="h-3 w-3" />
                                <span className="text-[11px] text-slate-500">보유</span>
                                <span className="text-[13px] font-bold tabular-nums text-slate-700">
                                  {eq.totalCount}
                                </span>
                              </span>
                              <span className="h-3 w-px bg-slate-200" />
                              <span className="flex items-center gap-1 text-slate-400">
                                <ClipboardCheck className="h-3 w-3" />
                                <span className="text-[11px] text-slate-500">점검</span>
                                <span className="text-[13px] font-bold tabular-nums text-slate-700">
                                  {eq.checkCount}
                                </span>
                              </span>
                              <span className="h-3 w-px bg-slate-200" />
                              <span className="flex items-center gap-1 text-slate-400">
                                <FileText className="h-3 w-3" />
                                <span className="text-[11px] text-slate-500">문서</span>
                                <span
                                  className={`text-[13px] font-bold tabular-nums ${colors.accent}`}
                                >
                                  {eq.docsCount || 0}
                                </span>
                              </span>
                            </div>
                            <Pencil className="ml-2 h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                          </button>
                        </div>

                        {/* 개별 문서 행 (펼침 시) + 스피너 */}
                        {(isExpanded && hasDocs) || addingEquipment?.key === eq.key ? (
                          <div className="border-t border-slate-100 bg-slate-50/30">
                            {docsIds.map((machineId: string, index: number) => {
                              const isInDeleteMode = deleteModeKey === eq.key;
                              const isDeleting = deletingMachineId === machineId;
                              return (
                                <button
                                  key={machineId}
                                  onClick={() =>
                                    isInDeleteMode
                                      ? handleDeleteDocClick(
                                          eq.key,
                                          machineId,
                                          `${eq.title} #${index + 1}`
                                        )
                                      : handleDocClick(eq.key, machineId)
                                  }
                                  disabled={isDeleting}
                                  className={`flex w-full items-center justify-between border-b border-slate-50 px-4 py-3 last:border-b-0 ${
                                    isInDeleteMode ? "active:bg-red-50" : colors.docActive
                                  } ${isDeleting ? "opacity-50" : ""}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={`flex h-7 w-7 items-center justify-center rounded-full ${colors.badge} text-xs font-bold ${colors.badgeText}`}
                                    >
                                      {index + 1}
                                    </div>
                                    <span className="text-sm font-medium text-slate-700">
                                      {eq.title} #{index + 1}
                                    </span>
                                  </div>
                                  {isDeleting ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-200 border-t-red-500" />
                                  ) : isInDeleteMode ? (
                                    <Trash2 className="h-4 w-4 text-red-400" />
                                  ) : (
                                    <svg
                                      className="h-4 w-4 text-slate-300"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                            {addingEquipment?.key === eq.key && (
                              <div className="flex w-full items-center border-b border-slate-50 px-4 py-3 last:border-b-0">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-7 w-7 items-center justify-center">
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                                  </div>
                                  <span className="text-sm text-slate-400">
                                    {addingEquipment.title} 추가 중...
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* 빈 상태 */}
        {categoryGroups.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-white py-12 shadow-sm">
            <div className="mb-3 rounded-full bg-slate-100 p-4">
              <svg
                className="h-8 w-8 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <p className="text-sm text-slate-500">설비 타입 정보를 불러올 수 없습니다.</p>
          </div>
        )}
      </div>

      {/* 수량 편집 모달 */}
      <EquipmentCountEditModal
        isOpen={isCountModalOpen}
        onClose={handleCountModalClose}
        onConfirm={handleCountConfirm}
        equipmentTitle={selectedForCount?.title ?? ""}
        initialTotalCount={selectedForCount?.totalCount ?? 0}
        initialCheckCount={selectedForCount?.checkCount ?? 0}
        isLoading={updating}
      />

      {/* 데모 설비 한도 안내 모달 */}
      <DemoLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitType="machine"
        currentCount={demoData?.demoStatus?.machineCount}
        maxCount={demoData?.demoStatus?.machineLimit}
      />
    </div>
  );
}
